import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Collapse,
  IconButton,
  Icon,
  Paper,
  Card,
} from '@mui/material';
import ArgonBox from 'components/ArgonBox';
import ArgonTypography from 'components/ArgonTypography';

// Helper to filter object keys based on an allowed list
const filterObjectByKeys = (raw, allowed) => {
  if (!raw || !allowed || !Array.isArray(allowed)) return {};
  return Object.keys(raw)
    .filter((key) => allowed.includes(key))
    .reduce((obj, key) => {
      obj[key] = raw[key];
      return obj;
    }, {});
};

const DiscountTiersTable = ({ tiers }) => {
  if (!tiers || tiers.length === 0) return null;
  return (
    <ArgonBox mt={2}>
      <ArgonTypography variant="subtitle2" fontWeight="bold" mb={1}>
        Discount Tiers
      </ArgonTypography>
      <TableContainer component={Paper} elevation={0} variant="outlined">
        <Table size="small">
          <TableHead sx={{ display: 'contents' }}>
            <TableRow>
              <TableCell>
                <ArgonTypography variant="caption" fontWeight="bold">
                  Tier Name
                </ArgonTypography>
              </TableCell>
              <TableCell>
                <ArgonTypography variant="caption" fontWeight="bold">
                  Lower Bound
                </ArgonTypography>
              </TableCell>
              <TableCell>
                <ArgonTypography variant="caption" fontWeight="bold">
                  Upper Bound
                </ArgonTypography>
              </TableCell>
              <TableCell>
                <ArgonTypography variant="caption" fontWeight="bold">
                  Discount
                </ArgonTypography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tiers.map((tier) => {
              const fields = tier.fields || {};
              const discountValue =
                fields.SBQQ__Discount__c != null
                  ? `${parseFloat(fields.SBQQ__Discount__c).toFixed(2)}%`
                  : fields.SBQQ__DiscountAmount__c != null
                  ? `$${parseFloat(fields.SBQQ__DiscountAmount__c).toFixed(2)}`
                  : 'N/A';
              return (
                <TableRow key={tier.id || tier.sfId}>
                  <TableCell>{tier.name}</TableCell>
                  <TableCell>{fields.SBQQ__LowerBound__c}</TableCell>
                  <TableCell>{fields.SBQQ__UpperBound__c || 'And up'}</TableCell>
                  <TableCell>{discountValue}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </ArgonBox>
  );
};

function OfferingDiscounts({ product, renderFieldsProp, basicFields }) {
  const [expandedItem, setExpandedItem] = useState({});

  const schedules = product?.related_data?.discountSchedules;

  console.log('[OfferingDiscounts] schedules', schedules);

  const scheduleOwners = Array.isArray(schedules)
    ? schedules
        .filter((ds) => ds && ds.objects && ds.type !== 'products') // 1. Filter out null/undefined, ensure objects array exists, and exclude product type
        .flatMap((ds) => ds.objects || []) // 2. Extract 'objects' array, defaulting to [] if missing, and flatten
        .flat() // 3. Additional flatten to ensure single level array
    : [];

  console.log('[OfferingDiscounts] scheduleOwners (filtered for products only)', scheduleOwners);

  if (scheduleOwners.length === 0) {
    return (
      <Card>
        <ArgonBox p={3}>
          <ArgonTypography variant="body2">No Discount Schedules available for this offering.</ArgonTypography>
        </ArgonBox>
      </Card>
    );
  }

  const handleItemClick = (itemId) => {
    setExpandedItem((prev) => ({ ...prev, [itemId]: !prev[itemId] }));
  };

  return (
    <ArgonBox>
      {scheduleOwners.map((owner) => {
        const schedule = owner.discountSchedules;
        if (!schedule) return null;

        const scheduleId = schedule.id || schedule.sfId;

        // --- THIS IS THE NEW LOGIC ---
        // Filter the schedule's main fields based on the visibility config
        const filteredFields = filterObjectByKeys(schedule.fields, basicFields);

        // Console log to show discount schedule types and details
        console.log('[OfferingDiscounts] Discount Schedule Details:', {
          scheduleId,
          scheduleName: schedule.name,
          scheduleFields: schedule.fields,
          discountType: schedule.fields?.SBQQ__Type__c || 'Unknown',
          discountMethod: schedule.fields?.SBQQ__DiscountMethod__c || 'Unknown',
          discountAmount: schedule.fields?.SBQQ__DiscountAmount__c,
          discountPercent: schedule.fields?.SBQQ__Discount__c,
          tiersCount: schedule.discountTiers?.length || 0,
          tiers: schedule.discountTiers,
        });

        return (
          <Card sx={{ marginBottom: '1rem' }} key={scheduleId}>
            <ArgonBox>
              <ArgonBox
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                onClick={() => handleItemClick(scheduleId)}
                style={{ cursor: 'pointer' }}
                borderRadius="md"
                p={1.5}
              >
                <ArgonBox>
                  <ArgonTypography variant="h6" fontWeight="medium" color="secondary">
                    {schedule.name}
                  </ArgonTypography>
                </ArgonBox>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleItemClick(scheduleId);
                  }}
                >
                  <Icon>{expandedItem[scheduleId] ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}</Icon>
                </IconButton>
              </ArgonBox>
              <Collapse in={expandedItem[scheduleId]} timeout="auto" unmountOnExit>
                <ArgonBox p={2}>
                  {/* --- RENDER THE SCHEDULE'S MAIN FIELDS --- */}
                  {renderFieldsProp(filteredFields)}
                  <DiscountTiersTable tiers={schedule.discountTiers} />
                </ArgonBox>
              </Collapse>
            </ArgonBox>
          </Card>
        );
      })}
    </ArgonBox>
  );
}

export default OfferingDiscounts;
